export type TaskOutputReference =
  | { kind: 'task'; taskReferenceName: string; path: string }
  | { kind: 'workflowInput'; field: string }
  | { kind: 'literal'; value: unknown }

const OUTPUT_DELIMITER = '.output.'
const WORKFLOW_INPUT_PREFIX = 'workflow.input.'

/**
 * Reads one value out of a task's input parameters and works out where it comes
 * from: another task's output, an input the workflow was started with, or a
 * plain value written into the definition.
 *
 *   '${a-1:SWITCH:getQuote.output.quote.customerId}' -> task, path 'quote.customerId'
 *   '${workflow.input.quoteId}'                      -> workflowInput, field 'quoteId'
 *   'PUBLISH_QUOTE'                                  -> literal
 *
 * We split on the first '.output.', not on '.'. A task reference name is built
 * from a node id and a task name, so it already has dots and colons in it, and
 * the path on the end can contain another '.output.' of its own.
 */
export const parseTaskOutputReference = (
  value: unknown
): TaskOutputReference => {
  const placeholder = unwrapPlaceholder(value)
  if (placeholder === undefined) return { kind: 'literal', value }

  if (placeholder.startsWith(WORKFLOW_INPUT_PREFIX)) {
    const field = placeholder.slice(WORKFLOW_INPUT_PREFIX.length)
    return field ? { kind: 'workflowInput', field } : { kind: 'literal', value }
  }

  const delimiterIndex = placeholder.indexOf(OUTPUT_DELIMITER)
  if (delimiterIndex === -1) return { kind: 'literal', value }

  const taskReferenceName = placeholder.slice(0, delimiterIndex)
  const path = placeholder.slice(delimiterIndex + OUTPUT_DELIMITER.length)
  if (!taskReferenceName || !path) return { kind: 'literal', value }

  return { kind: 'task', taskReferenceName, path }
}

const unwrapPlaceholder = (value: unknown): string | undefined => {
  const isPlaceholder =
    typeof value === 'string' && value.startsWith('${') && value.endsWith('}')

  return isPlaceholder ? value.slice(2, -1) : undefined
}
